import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import CustomSelect from "./ui/CustomSelect";
import { TextWrap } from "./ui/TextWrap";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const ClientRoles = () => {
  const [clientRoles, setClientRoles] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: "client_name",
    direction: "asc",
  });
  const navigate = useNavigate();

  // Debounce timer ref
  const debounceTimer = useRef(null);

  const getAuthToken = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setErrorMessage("Please log in to continue.");
    }
    return token;
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const endpoint = `/get-all-clients?page=1&per_page=1000&search=&sort_by=client_id&sort_order=desc`;
      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const clientList = response.data.data || [];
        setClients(clientList);
      } else {
        console.error("Failed to load clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchClientRoles = useCallback(
    async (
      page = 1,
      perPage = 10,
      search = "",
      sortBy = "client_name",
      sortOrder = "asc",
      clientId = ""
    ) => {
      try {
        setLoading(true);
        setErrorMessage("");
        let endpoint = `/get-all-client-roles-bo?search=${encodeURIComponent(
          search
        )}&page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_order=${sortOrder}`;

        if (clientId) {
          endpoint += `&client_id=${encodeURIComponent(clientId)}`;
        }

        const response = await apiCall("get", endpoint, null, navigate);

        if (response?.data?.success) {
          const roleList = (response.data.data || []).map((role) => ({
            ...role,
            display_created_datetime: role.created_datetime
              ? new Date(role.created_datetime).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
              : "-",
          }));
          const pagination = response.data.pagination || {};
          const total = pagination.total || 0;
          const currentPageFromResponse = pagination.current_page || page;
          const lastPage = pagination.last_page || 1;
          const perPageFromResponse = pagination.per_page || perPage;

          setClientRoles(roleList);
          setTotalEntries(total);
          setCurrentPage(parseInt(currentPageFromResponse, 10));
          setTotalPages(parseInt(lastPage, 10));
          setPerPage(parseInt(perPageFromResponse, 10));
        } else {
          setErrorMessage("Failed to load client roles.");
          setClientRoles([]);
          setTotalEntries(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching client roles:", error);
        setErrorMessage(error.message || "Error fetching client roles.");
        setClientRoles([]);
        setTotalEntries(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  // Clients sirf ek baar load honge
  useEffect(() => {
    fetchClients();
  }, []);

  // Main effect - har dependency change pe call hoga (WITH DEBOUNCE for search)
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Search ke liye debounce, baaki ke liye instant
    const delay = searchQuery ? 500 : 0;

    debounceTimer.current = setTimeout(() => {
      fetchClientRoles(
        currentPage,
        perPage,
        searchQuery,
        sortConfig.field,
        sortConfig.direction,
        selectedClient
      );
    }, delay);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [
    currentPage,
    perPage,
    searchQuery,
    sortConfig.field,
    sortConfig.direction,
    selectedClient,
    fetchClientRoles,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    // useEffect apne aap call karega with debounce
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected?.value || "");
    setCurrentPage(1);
    // useEffect apne aap call karega
  };

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
    // useEffect apne aap call karega
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    setCurrentPage(1);
    // useEffect apne aap call karega
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // useEffect apne aap call karega
    }
  };

  const sortedClientRoles = [...clientRoles].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "is_active") {
      const valA = valueA ? "Active" : "Inactive";
      const valB = valueB ? "Active" : "Inactive";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    if (sortConfig.field === "created_datetime") {
      const dateA = valueA ? new Date(valueA).getTime() : 0;
      const dateB = valueB ? new Date(valueB).getTime() : 0;
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  if (errorMessage) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by Role Name..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
            />
            <div style={{ minWidth: "200px" }}>
              <CustomSelect
                name="client"
                value={selectedClient}
                onChange={handleClientChange}
                options={clients}
                placeholder="Select Client"
                labelKey="client_name"
                valueKey="client_id"
                isLoading={clientsLoading}
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() => "No clients found"}
              />
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {sortedClientRoles.length === 0 && !loading ? (
            <TableDataNoAvailable message="No client roles found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>

                     <th
                        scope="col"
                        onClick={() => handleSort("company_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("role_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Role Name</span>
                          <SortIcons
                            field="role_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("description")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Description</span>
                          <SortIcons
                            field="description"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_datetime")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("is_active")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClientRoles.map((role, index) => (
                      <tr key={role.client_role_id || index}>
                        <td className="p-2">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="p-2">
                          <Link
                            to={`/all-clients`}
                            className="text-primary-600"
                          >
                            {role.company_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">
                          <Link
                            to={`/all-clients`}
                            className="text-primary-600"
                          >
                            {role.client_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">{role.role_name || "-"}</td>
                        <td className="p-2" title={role.description || "-"}>
                          <TextWrap location={role.description || "-"} />
                        </td>
                        <td className="p-2">{role.display_created_datetime}</td>
                        <td className="p-2 ">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              role.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {role.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <div className="">
                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                    totalPages: totalPages,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientRoles;
